import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, Clock3, MessageCircle, Rss, SearchCheck, Server, Shield, Wrench } from "lucide-react";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { BLOG_POSTS } from "../mock/blog";
import { WHATSAPP_LINK } from "../config/contact";

const FEATURED_PRIORITY = [
  "WordPress hackeado",
  "Seguridad WordPress",
  "Mantenimiento",
  "Guías de prevención",
  "SEO Técnico",
  "Rendimiento",
  "Casos reales"
];

const CATEGORY_NAVIGATION = [
  { name: "SEO Técnico", anchor: "categoria-seo-tecnico" },
  { name: "Seguridad WordPress", anchor: "categoria-seguridad-wordpress" },
  { name: "Mantenimiento", anchor: "categoria-mantenimiento" },
  { name: "WordPress hackeado", anchor: "categoria-wordpress-hackeado" },
  { name: "Rendimiento", anchor: "categoria-rendimiento" },
  { name: "Casos reales", anchor: "categoria-casos-reales" },
  { name: "Guías de prevención", anchor: "categoria-guias-prevencion" }
];

const FOOTER_EDITORIAL_TOPICS = [
  "WordPress hackeado",
  "malware",
  "seguridad",
  "hardening",
  "mantenimiento",
  "SEO técnico",
  "Core Web Vitals",
  "rendimiento"
];

export default function Blog() {
  const sortedPosts = [...BLOG_POSTS].sort((a, b) => new Date(b.date) - new Date(a.date));
  const featuredPosts = [...sortedPosts]
    .sort((a, b) => {
      const scoreA = FEATURED_PRIORITY.indexOf(a.category);
      const scoreB = FEATURED_PRIORITY.indexOf(b.category);
      if (scoreA !== scoreB) return scoreA - scoreB;
      return new Date(b.date) - new Date(a.date);
    })
    .slice(0, 3);
  const featuredSlugs = new Set(featuredPosts.map((post) => post.slug));
  const recentPosts = sortedPosts.filter((post) => !featuredSlugs.has(post.slug));
  const categorySections = CATEGORY_NAVIGATION
    .map((category) => ({
      ...category,
      posts: sortedPosts.filter((post) => post.category === category.name)
    }))
    .filter((category) => category.posts.length > 0);

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog WPRescate24",
    "url": "https://wprescate24.com/blog",
    "description": "Guías técnicas de seguridad, recuperación, mantenimiento y SEO WordPress con enfoque aplicable."
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": sortedPosts.map((post, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": `https://wprescate24.com/blog/${post.slug}`,
      "name": post.title
    }))
  };

  return (
    <>
      <SEO
        title="Blog WordPress: Diagnóstico, Seguridad, Recuperación y SEO Técnico"
        description="Guías técnicas para incidentes WordPress: hackeos, caídas, mantenimiento, SEO técnico y rendimiento."
        keywords="wordpress hackeado, seguridad wordpress, mantenimiento wordpress, seo técnico wordpress, core web vitals"
        canonicalUrl="https://wprescate24.com/blog"
        structuredData={[blogSchema, itemListSchema]}
      />
      <div className="dark min-h-screen bg-background">
        <Header
          currentPage="blog"
          internalLinks={[
            { href: "#destacados", label: "Destacados" },
            { href: "#recientes", label: "Recientes" },
            { href: "#categorias-nav", label: "Categorías" },
            { href: "#contacto-blog", label: "Contacto" }
          ]}
          ctaText="Contactar"
          ctaHref="#contacto-blog"
          ctaColor="secondary"
        />

        <main id="main-content">
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
          <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Rss className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Centro de conocimiento WordPress</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight max-w-4xl mx-auto">
              Diagnóstico y guías para WordPress hackeado, caído o con degradación crítica
            </h1>
            <p className="mt-6 text-lg text-foreground/80 max-w-3xl mx-auto leading-relaxed">
              Contenido técnico para identificar causa raíz, contener impacto y recuperar operación con menor riesgo.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button className="bg-secondary hover:bg-secondary/90 text-white font-bold" asChild>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Mi WordPress tiene problemas
                </a>
              </Button>
              <Button variant="outline" className="border-primary/40 text-primary hover:bg-primary/10 font-semibold" asChild>
                <a href="#categorias-nav">Navegar por categoría</a>
              </Button>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-primary/5">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs md:text-sm text-foreground/80">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-secondary" /> Respuesta a incidentes WordPress</div>
              <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-secondary" /> Casos documentados en producción</div>
              <div className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-secondary" /> Atención técnica por WhatsApp</div>
              <div className="flex items-center gap-2"><Clock3 className="w-4 h-4 text-secondary" /> Actualización técnica semanal</div>
            </div>
          </div>
        </section>

        <section id="categorias-nav" className="py-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 sticky top-[73px] z-30">
          <div className="max-w-7xl mx-auto px-6">
            <div className="overflow-x-auto">
              <div className="inline-flex gap-2 min-w-max">
                {categorySections.map((category) => (
                  <a
                    key={category.anchor}
                    href={`#${category.anchor}`}
                    className="text-sm px-3 py-2 rounded-full border border-border bg-background hover:border-primary/50 hover:text-primary transition-colors"
                  >
                    {category.name} ({category.posts.length})
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="destacados" className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Guías críticas (Top 3)</h2>
              <p className="mt-4 text-lg text-foreground/70">
                Lectura prioritaria para incidentes de seguridad, caídas y pérdida de visibilidad.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {featuredPosts.map((post) => (
                <Card key={post.slug} className="bg-background border-2 border-border hover:border-primary/40 hover:shadow-xl transition-all">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <Badge className="bg-primary/10 text-primary hover:bg-primary/15 border border-primary/20">{post.category}</Badge>
                      <span className="text-xs text-foreground/60">{post.readTime}</span>
                    </div>
                    <CardTitle className="text-xl text-foreground leading-tight">
                      <RouterLink to={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                        {post.title}
                      </RouterLink>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-foreground/75 leading-relaxed">{post.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="text-xs text-foreground/60">{post.date}</span>
                      <RouterLink to={`/blog/${post.slug}`} className="text-sm text-primary font-semibold hover:opacity-80">
                        Leer guía →
                      </RouterLink>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="recientes" className="py-20 bg-background border-t border-border">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Artículos recientes</h2>
              <p className="mt-3 text-foreground/70">Resumen breve, lectura estimada y acceso directo a la guía completa.</p>
            </div>
            <div className="space-y-4">
              {recentPosts.map((post) => (
                <Card key={`recent-${post.slug}`} className="border border-border bg-background">
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <Badge className="bg-primary/10 text-primary border border-primary/20">{post.category}</Badge>
                      <span className="text-xs text-foreground/60">{post.date}</span>
                      <span className="text-xs text-foreground/60">{post.readTime}</span>
                    </div>
                    <RouterLink to={`/blog/${post.slug}`} className="text-lg font-semibold text-foreground hover:text-primary transition-colors">
                      {post.title}
                    </RouterLink>
                    <p className="mt-2 text-sm text-foreground/75">{post.excerpt}</p>
                    <RouterLink to={`/blog/${post.slug}`} className="mt-3 inline-flex text-sm text-primary font-semibold hover:opacity-80">
                      Leer guía →
                    </RouterLink>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="categorias" className="py-20 bg-background border-t border-border">
          <div className="max-w-6xl mx-auto px-6 space-y-5">
            {categorySections.map((category) => (
              <Card key={category.anchor} id={category.anchor} className="border border-border bg-background">
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-xl text-foreground">{category.name}</CardTitle>
                    <span className="text-xs text-primary font-semibold">{category.posts.length} artículos</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-3">
                    {category.posts.slice(0, 4).map((post) => (
                      <RouterLink
                        key={`${category.anchor}-${post.slug}`}
                        to={`/blog/${post.slug}`}
                        className="rounded-lg border border-border p-3 hover:border-primary/40 transition-colors"
                      >
                        <p className="text-sm font-semibold text-foreground leading-snug">{post.title}</p>
                        <p className="mt-1 text-xs text-foreground/60">{post.date} · {post.readTime}</p>
                      </RouterLink>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="py-16 bg-background border-t border-border">
          <div className="max-w-5xl mx-auto px-6">
            <Card className="border border-border bg-background">
              <CardContent className="p-7">
                <h3 className="text-2xl font-bold text-foreground">Criterio editorial técnico</h3>
                <p className="mt-3 text-foreground/80">
                  Cada publicación parte de escenarios reales de operación WordPress, no de contenido teórico.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-foreground/75">
                  <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 text-secondary" /> Cobertura de incidentes: hackeos, malware, errores críticos y caídas.</li>
                  <li className="flex items-start gap-2"><SearchCheck className="w-4 h-4 mt-0.5 text-secondary" /> Enfoque en causa raíz, validaciones y control de impacto.</li>
                  <li className="flex items-start gap-2"><Wrench className="w-4 h-4 mt-0.5 text-secondary" /> Recomendaciones aplicables para pyme, ecommerce y agencias.</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="contacto-blog" className="py-16 bg-background border-t border-border">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground">¿Tu WordPress tiene errores, hackeos o caídas?</h3>
            <p className="mt-3 text-foreground/75">
              Si necesitas diagnóstico, revisamos tu caso por WhatsApp y definimos siguiente acción técnica.
            </p>
            <div className="mt-6">
              <Button className="bg-secondary hover:bg-secondary/90 text-white font-bold px-8 py-4" asChild>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Revisar mi caso por WhatsApp
                </a>
              </Button>
            </div>
            <p className="mt-3 text-xs text-foreground/60">Respuesta humana del equipo técnico.</p>
          </div>
        </section>

        <section id="keywords" className="py-8 bg-background border-t border-border">
          <div className="max-w-5xl mx-auto px-6">
            <p className="text-xs text-foreground/60 leading-relaxed">
              Cobertura temática: {FOOTER_EDITORIAL_TOPICS.join(" · ")}
            </p>
          </div>
        </section>
        </main>

        <footer className="bg-primary text-white">
          <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-6 h-6 text-[#b8cd37]" />
                  <span className="font-bold text-white text-lg">WPRescate24</span>
                </div>
                <p className="text-sm text-white/70">
                  Agencia especializada en: optimización, rescate, seguridad y blindaje de WordPress y sitios web.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-white mb-3">Servicios</h4>
                <div className="flex flex-col gap-2">
                  <RouterLink to="/" className="text-sm text-white/70 hover:text-white transition-colors">Rescate WordPress</RouterLink>
                  <RouterLink to="/mantenimiento-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Mantenimiento Proactivo</RouterLink>
                  <RouterLink to="/hosting-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Hosting WordPress</RouterLink>
                  <RouterLink to="/blog" className="text-sm text-white/70 hover:text-white transition-colors">Blog</RouterLink>
                </div>
              </div>
              <div>
                <h4 className="font-bold text-white mb-3">Infraestructura</h4>
                <p className="text-sm text-white/70 mb-2">Nuestros servicios están respaldados por:</p>
                <a
                  href="https://servidores.garey.mx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#b8cd37] hover:text-[#c6da45] transition-colors"
                >
                  <Server className="w-4 h-4" />
                  servidores.garey.mx
                </a>
              </div>
            </div>
            <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row items-center justify-between text-sm text-white/60">
              <div>© {new Date().getFullYear()} WPRescate24. Todos los derechos reservados. Una empresa de <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-white hover:underline font-semibold">GAREY AW</a></div>
              <div className="flex items-center gap-6 mt-3 md:mt-0">
                <RouterLink to="/terminos" className="hover:text-white transition-colors">Términos</RouterLink>
                <RouterLink to="/privacidad" className="hover:text-white transition-colors">Privacidad</RouterLink>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="hover:text-[#b8cd37] transition-colors">WhatsApp</a>
              </div>
            </div>
          </div>
        </footer>

        <a href={WHATSAPP_LINK} target="_blank" rel="noopener" aria-label="Abrir WhatsApp" className="fixed bottom-6 right-6 z-50 group">
          <div className="h-16 w-16 rounded-full bg-[#25D366] hover:bg-[#20bd5a] flex items-center justify-center shadow-2xl border-2 border-white/30 transition-all hover:scale-110">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
        </a>
      </div>
    </>
  );
}
